import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ArrowRightIcon, CheckCircle2Icon, TrendingDownIcon, DollarSignIcon, ClockIcon, DownloadIcon } from 'lucide-react';
import { ScenarioData, AccentColor } from './ScenarioCard';
import { HeaderData } from './Header';
import { formatCurrency, formatNumber } from '../utils/format';
import { generatePdf } from '../utils/generatePdf';
interface SelectedSummaryProps {
  scenario: ScenarioData | null;
  currentTotalPayment: number;
  headerData: HeaderData;
  onClear: () => void;
}
const accentStyles: Record<AccentColor, {
  bg: string;
  text: string;
  border: string;
  lightBg: string;
  iconBg: string;
}> = {
  blue: {
    bg: 'bg-blue-600',
    text: 'text-blue-700',
    border: 'border-blue-200',
    lightBg: 'bg-blue-50',
    iconBg: 'bg-blue-100 text-blue-600'
  },
  emerald: {
    bg: 'bg-emerald-600',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
    lightBg: 'bg-emerald-50',
    iconBg: 'bg-emerald-100 text-emerald-600'
  },
  amber: {
    bg: 'bg-amber-600',
    text: 'text-amber-700',
    border: 'border-amber-200',
    lightBg: 'bg-amber-50',
    iconBg: 'bg-amber-100 text-amber-600'
  },
  violet: {
    bg: 'bg-violet-600',
    text: 'text-violet-700',
    border: 'border-violet-200',
    lightBg: 'bg-violet-50',
    iconBg: 'bg-violet-100 text-violet-600'
  }
};
export function SelectedSummary({
  scenario,
  currentTotalPayment,
  headerData,
  onClear
}: SelectedSummaryProps) {
  if (!scenario) return null;
  const styles = accentStyles[scenario.accent];
  const newTotalPayment = scenario.paymentBreakdown.newFirstPayment + scenario.paymentBreakdown.mortgageInsurance + scenario.paymentBreakdown.taxesInsurance;
  return <AnimatePresence data-id="element-166">
      <motion.div initial={{
      opacity: 0,
      height: 0,
      marginBottom: 0
    }} animate={{
      opacity: 1,
      height: 'auto',
      marginBottom: 32
    }} exit={{
      opacity: 0,
      height: 0,
      marginBottom: 0
    }} transition={{
      duration: 0.4,
      ease: 'easeOut'
    }} className="overflow-hidden" data-id="element-167">
        <div className={`bg-white rounded-2xl shadow-lg border-2 ${styles.border} overflow-hidden relative`} data-id="element-168">
          {/* Top accent bar */}
          <div className={`h-2 w-full ${styles.bg}`} data-id="element-169"></div>

          <div className="p-6 sm:p-8" data-id="element-170">
            <div className="flex justify-between items-start mb-6" data-id="element-171">
              <div className="flex items-center gap-3" data-id="element-172">
                <div className={`p-2 rounded-full ${styles.iconBg}`} data-id="element-173">
                  <CheckCircle2Icon className="w-6 h-6" data-id="element-174" />
                </div>
                <div data-id="element-175">
                  <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-176">
                    Selected Option
                  </h2>
                  <div className="flex items-center gap-3" data-id="element-177">
                    <h3 className={`text-2xl font-bold ${styles.text}`} data-id="element-178">
                      {scenario.title}
                    </h3>
                    <span className={`px-3 py-1 rounded-full text-sm font-bold ${styles.lightBg} ${styles.text}`} data-id="element-179">
                      {scenario.rate}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2" data-id="element-180">
                <button onClick={() => generatePdf(scenario, headerData)} className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white text-sm font-semibold rounded-lg hover:bg-slate-700 transition-colors" data-id="element-181">
                  <DownloadIcon className="w-4 h-4" data-id="element-182" />
                  Download PDF
                </button>
                <button onClick={onClear} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors" aria-label="Clear selection" data-id="element-183">
                  <XIcon className="w-5 h-5" data-id="element-184" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-185">
              {/* Payment Change */}
              <div className="bg-gray-50 rounded-xl p-5 border border-gray-100 flex flex-col justify-center" data-id="element-186">
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 text-center" data-id="element-187">
                  Payment Change
                </p>
                <div className="flex items-center justify-between" data-id="element-188">
                  <div className="text-center" data-id="element-189">
                    <p className="text-xs text-gray-500 mb-1" data-id="element-190">Current</p>
                    <p className="text-xl font-semibold text-gray-900" data-id="element-191">
                      {formatCurrency(currentTotalPayment)}
                    </p>
                  </div>
                  <ArrowRightIcon className="w-5 h-5 text-gray-400" data-id="element-192" />
                  <div className="text-center" data-id="element-193">
                    <p className="text-xs text-gray-500 mb-1" data-id="element-194">New</p>
                    <p className="text-xl font-bold text-emerald-600" data-id="element-195">
                      {formatCurrency(newTotalPayment)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Key Benefits */}
              <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-100 flex flex-col justify-center items-center text-center" data-id="element-196">
                <div className="bg-emerald-100 p-2 rounded-full mb-2" data-id="element-197">
                  <TrendingDownIcon className="w-5 h-5 text-emerald-600" data-id="element-198" />
                </div>
                <p className="text-sm font-medium text-emerald-800 mb-1" data-id="element-199">
                  You save
                </p>
                <p className="text-3xl font-bold text-emerald-600" data-id="element-200">
                  {formatCurrency(scenario.metrics.monthlySavings)}
                </p>
                <p className="text-xs text-emerald-700 mt-1 font-medium" data-id="element-201">
                  every month
                </p>
              </div>

              {/* Costs & Breakeven */}
              <div className="bg-slate-50 rounded-xl p-5 border border-slate-200 flex flex-col justify-center space-y-4" data-id="element-202">
                <div className="flex items-start gap-3" data-id="element-203">
                  <DollarSignIcon className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" data-id="element-204" />
                  <div data-id="element-205">
                    <p className="text-xs text-slate-500 font-medium uppercase tracking-wider" data-id="element-206">
                      Total Costs
                    </p>
                    <p className="text-lg font-bold text-slate-900" data-id="element-207">
                      {formatCurrency(scenario.estimatedCosts.totalCost)}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5" data-id="element-208">
                      Cash needed:{' '}
                      <span className={scenario.paymentBreakdown.cashNeeded > 0 ? 'text-red-600 font-medium' : 'text-slate-700 font-medium'} data-id="element-209">
                        {formatCurrency(scenario.paymentBreakdown.cashNeeded)}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-210">
                  <ClockIcon className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" data-id="element-211" />
                  <div data-id="element-212">
                    <p className="text-xs text-slate-500 font-medium uppercase tracking-wider" data-id="element-213">
                      Breakeven
                    </p>
                    <p className="text-lg font-bold text-slate-900" data-id="element-214">
                      {formatNumber(scenario.estimatedCosts.breakeven)}{' '}
                      <span className="text-sm font-medium text-slate-500" data-id="element-215">
                        months
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 text-center" data-id="element-216">
              <p className="text-sm text-gray-500" data-id="element-217">
                Over 5 years, this option saves you{' '}
                <span className="font-bold text-emerald-600" data-id="element-218">
                  {formatCurrency(scenario.metrics.fiveYearSavings)}
                </span>
                .
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>;
}